import React, { useState, useEffect } from 'react';
import { Menu } from 'lucide-react';
import { navItems } from '../data/mock';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      
      // Update active section based on scroll position
      const sections = navItems.map(item => item.id);
      for (const section of sections.reverse()) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 150) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Logo Component - Clean, crisp design
  const Logo = ({ size = 'default' }) => {
    const symbolSize = size === 'large' ? 'h-12 w-12' : 'h-9 w-9';
    const textSize = size === 'large' ? 'text-xl' : 'text-lg';
    
    return (
      <div className="flex items-center gap-3">
        {/* Symbol from actual logo */}
        <img 
          src="/adela-logo-symbol.png" 
          alt="" 
          className={`${symbolSize} object-contain`}
          aria-hidden="true"
        />
        {/* Styled company name */}
        <div className={`${textSize} font-bold tracking-wide`}>
          <span className="text-white">ADELA</span>
          <span className="text-amber-400 ml-1.5">MERCHANTS</span>
        </div>
      </div>
    );
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-neutral-950/95 backdrop-blur-md shadow-lg shadow-black/20'
          : 'bg-transparent'
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a
            href="#home"
            onClick={(e) => {
              e.preventDefault();
              scrollToSection('#home');
            }}
            className="flex items-center group hover:opacity-90 transition-opacity"
          >
            {/* Full logo for larger screens */}
            <div className="hidden sm:block">
              <Logo />
            </div>
            {/* Symbol only for mobile */}
            <img 
              src="/adela-logo-symbol.png" 
              alt="Adela Merchants" 
              className="sm:hidden h-10 w-10 object-contain"
            />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <a
                key={item.id}
                href={item.href}
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection(item.href);
                }}
                className={`relative px-4 py-2 text-sm font-medium transition-all duration-300 rounded-lg ${
                  activeSection === item.id
                    ? 'text-amber-400'
                    : 'text-neutral-300 hover:text-white'
                }`}
              >
                {item.label}
                {activeSection === item.id && (
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 bg-amber-400 rounded-full" />
                )}
              </a>
            ))}
            <a
              href="#contact"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection('#contact');
              }}
              className="ml-4 px-5 py-2.5 bg-amber-400 text-neutral-900 font-semibold text-sm rounded-lg hover:bg-amber-300 transition-colors duration-300"
            >
              Get Quote
            </a>
          </div>

          {/* Mobile Navigation */}
          <Sheet>
            <SheetTrigger asChild>
              <button className="md:hidden p-2 text-white hover:text-amber-400 transition-colors">
                <Menu className="w-6 h-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-neutral-950 border-neutral-800 w-72">
              <div className="flex flex-col gap-6 mt-8">
                {/* Logo in mobile menu - Clean styled version */}
                <div className="flex items-center gap-3 mb-4">
                  <img 
                    src="/adela-logo-symbol.png" 
                    alt="" 
                    className="h-10 w-10 object-contain"
                  />
                  <div className="text-lg font-bold tracking-wide">
                    <span className="text-white">ADELA</span>
                    <span className="text-amber-400 ml-1.5">MERCHANTS</span>
                  </div>
                </div>
                {navItems.map((item) => (
                  <a
                    key={item.id}
                    href={item.href}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection(item.href);
                    }}
                    className={`text-lg font-medium transition-colors ${
                      activeSection === item.id
                        ? 'text-amber-400'
                        : 'text-neutral-300 hover:text-white'
                    }`}
                  >
                    {item.label}
                  </a>
                ))}
                <a
                  href="#contact"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection('#contact');
                  }}
                  className="mt-4 px-5 py-3 bg-amber-400 text-neutral-900 font-semibold text-center rounded-lg hover:bg-amber-300 transition-colors"
                >
                  Get Quote
                </a>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
};

export default Navbar;
